import { ReactNode, useEffect, useCallback } from 'react';
import { kc } from '../auth/keycloak';
import { connectSocket, disconnectSocket, socket } from '../api/socket';
import { useToastContext } from '../contexts/useToast';
import { useNotifications, type Notification } from '../contexts/NotificationsContext';
import type { Toast } from '../contexts/ToastContext.types';
interface Props {
    children: ReactNode;
}

export const WebSocketProvider = ({ children }: Props) => {
    const { addToast } = useToastContext();
    const { addPushedNotification } = useNotifications();

    const handleToast = useCallback((payload: Toast) => {
        if (!payload || typeof payload.message !== 'string') return;
        const severity = payload.severity ?? 'info';
        addToast(payload.message, severity, payload.duration);
    }, [addToast]);

    const handleNotification = useCallback((payload: Notification) => {
        if (!payload || typeof payload.measurementName !== 'string') return;
        addPushedNotification(payload);
    }, [addPushedNotification]);

    useEffect(() => {
        if (!kc.authenticated || socket.connected) return;

        connectSocket();

        const handleConnect = () => {
            console.debug('[socket] connected');
        };
        const handleDisconnect = (reason: string) => {
            console.debug('[socket] disconnected:', reason);
        };
        const handleConnectError = (err: Error) => {
            console.error('[socket] connection error:', err.message);
        };

        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on('connect_error', handleConnectError);
        socket.on('toast', handleToast);
        socket.on('notification', handleNotification);

        return () => {
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
            socket.off('connect_error', handleConnectError);
            socket.off('toast', handleToast);
            socket.off('notification', handleNotification);
            disconnectSocket();
        };
    }, [handleToast, handleNotification]);

    return <>{children}</>;
};